// The customer-case SLA countdown — the ticking clock a case carries on the driver's
// portal and on the office triage list.
//
// Counted in WORKING days, not calendar days (Paul, 24 Aug 2026). A case that fell due
// on a Friday is one working day late on the Monday, not three: nobody was on the road
// over the weekend, so counting Saturday and Sunday against the driver reads as a bigger
// failure than it is. Same for a public holiday. The DEADLINE itself is untouched — it's
// AP's SLA target date and ours to meet — only the elapsed/remaining count skips the days
// the depot doesn't run.
//
// Weekday-only, less NSW public holidays, from the one source of truth in leaveRules.
import { workingDayCount } from './leaveRules';

const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const shiftDay = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return isoOf(d); };

// { show, color, label, overdue, days } — or null when the case has no SLA target.
// `days` is whole working days (0 when the deadline is today, or when only a weekend
// has passed since it), so the label falls back to hours in those cases.
export function caseDueMeta(due_at, now = new Date()) {
  if (!due_at) return null;
  const due = new Date(due_at);
  if (isNaN(due.getTime())) return null;
  const ms = due.getTime() - now.getTime();
  const overdue = ms < 0;
  const hrs = Math.abs(ms) / 3600000;

  const dueISO = isoOf(due);
  const todayISO = isoOf(now);
  // Working days strictly BETWEEN the two dates' endpoints: from the day after the
  // earlier date up to and including the later one.
  const days = overdue
    ? (todayISO > dueISO ? workingDayCount(shiftDay(dueISO, 1), todayISO) : 0)
    : (dueISO > todayISO ? workingDayCount(shiftDay(todayISO, 1), dueISO) : 0);

  let label;
  if (overdue) label = days > 0 ? `${days}d overdue` : (hrs < 24 ? `${Math.max(1, Math.round(hrs))}h overdue` : 'overdue');
  else if (days > 0) label = `${days}d left`;
  else if (hrs < 1) label = `${Math.max(1, Math.round(hrs * 60))}m left`;
  else label = `${Math.round(hrs)}h left`;

  // Hidden while there's still a clear working day in hand; amber as it closes, red once
  // it's on the day or past it.
  const show = overdue || days <= 1;
  const color = overdue || days === 0 ? '#ef4444' : days === 1 ? '#f59e0b' : 'var(--hp-dim)';
  return { show, color, label, overdue, days };
}
